import { FunctionComponent, useMemo, useState } from 'react';
import { Loader } from '~components/Loader';
import { Pagination, PaginationProps } from 'antd';
import { useParams, useLocation } from 'react-router-dom';
import { useGetNews, useGetNewsCount } from '~components/hooks';
import { News } from '~components/blog';
import { NetworkStatus } from '@apollo/client';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import NotFound from '~frontend/pages/error/NotFound';
import { useHttpClient } from '~shared/frontend/providers/HttpProvider';

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

const itemRender: PaginationProps['itemRender'] = (
  _,
  type,
  originalElement,
) => {
  if (type === 'prev') {
    return (
      <a className="pagination-prev">
        <svg
          width="8"
          height="12"
          viewBox="0 0 8 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.73982 6L7.57922 1.16053L6.41929 -2.62241e-07L0.419922 5.99947L1.57989 7.16L6.41929 12L7.57922 10.8395L2.73982 6Z"
            fill="#CCCCCC"
          ></path>
        </svg>
      </a>
    );
  }
  if (type === 'next') {
    return (
      <a className="pagination-next">
        <svg
          width="8"
          height="12"
          viewBox="0 0 8 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.73982 6L7.57922 1.16053L6.41929 -2.62241e-07L0.419922 5.99947L1.57989 7.16L6.41929 12L7.57922 10.8395L2.73982 6Z"
            fill="#CCCCCC"
          ></path>
        </svg>
      </a>
    );
  }
  return originalElement;
};

export const Content: FunctionComponent = () => {
  const { setHttpContext } = useHttpClient();
  const { tag, search } = useParams();
  const query = useQuery();
  const [page, setPage] = useState(Number(query.get('page')) || 1);

  const {
    i18n: { language },
  } = useTranslation();

  let where: any = {
    status: { equals: 'published' },
    language: { equals: language },
  };

  if (tag || !search) {
    where = {
      ...where,
      tags: { some: { name: { equals: tag || 'news' } } },
    };
  } else if (search) {
    where = {
      OR: [
        { title: { contains: search, mode: 'insensitive' } },
        { description: { contains: search, mode: 'insensitive' } },
      ],
    };
  }

  const getNews = useGetNews({
    where,
    orderBy: [{ publishDate: 'desc' }],
    take: 7,
    skip: 7 * (page - 1),
  });
  const getNewsCount = useGetNewsCount({
    where,
  });

  const onChange = async (page: number, pageSize: number) => {
    setPage(page);
    await getNews.fetchMore({
      variables: {
        skip: 7 * (page - 1),
      },
    });
  };

  const loading =
    (getNews?.loading && getNews?.networkStatus === NetworkStatus.loading) ||
    getNewsCount?.loading;
  const error = getNews?.error || getNewsCount?.error;

  const posts = useMemo(() => {
    if (loading) {
      return <Loader />;
    } else if (error) {
      return <>Blog | Error! {error.message}</>;
    }

    if (tag && _.size(getNews?.data?.posts) === 0) {
      setHttpContext({ redirect: '/' });
      return <NotFound />;
    }

    return _(getNews?.data?.posts)
      .map((getNews, index) => (
        <News key={getNews.id} getNews={getNews} index={index} />
      ))
      .value();
  }, [getNews?.data?.posts, loading, error]);

  return (
    <>
      {posts}
      {getNewsCount?.data?.postsCount > 0 && (
        <Pagination
          onChange={onChange}
          itemRender={itemRender}
          className="pagination"
          showLessItems
          showQuickJumper={false}
          defaultCurrent={page}
          showSizeChanger={false}
          total={getNewsCount?.data?.postsCount}
        />
      )}
    </>
  );
};
